import {
  DARK_MODE_KEY,
  LAST_AUTHENTICATED_USER,
  MAP_FILTERS_VISIBLE_KEY,
  NAV_OPEN_FLAG_KEY,
  POOLING_MAP_TOGGLE_TRAFFIC_LAYER_KEY,
  REACT_I18NEXT_LANGUAGE_KEY,
  REGIONS_KEY,
  SERVICE_AREA_MANAGEMENT_SERVICE_AREA_ID_KEY,
} from '@backoffice-frontend/common'
import { RestrictedContext } from '@backoffice-frontend/restricted'
import { Claims, FromBoumWireClaims } from '@moia-dev/moia-token-claims'
import type { AuthSession, AuthUser } from 'aws-amplify/auth'
import { jwtDecode } from 'jwt-decode'
import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import store from 'store'
import {
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser,
  Hub,
  signOut,
} from './amplify'
import { configureAmplify } from './configureAmplify'

type AuthContextProps = {
  claims: Claims | null
  token: string | null
  isAuthenticated: boolean
  loading: boolean
  hasMFA: boolean | null
  checkAuthState: () => Promise<void>
}
export const AuthContext = createContext<AuthContextProps | null>(null)

type AuthContextProviderProps = {
  children: ReactNode
  userPoolClientId: string
}

export const AuthContextProvider = ({
  children,
  userPoolClientId,
}: AuthContextProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<AuthSession | null>(null)
  const [hasMFA, setHasMFA] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)

  const isAuthenticated = !!user
  const token = session?.tokens?.idToken?.toString() ?? null

  const checkAuthState = useCallback(async () => {
    try {
      const [currentUser, authSession, mfaPreference] = await Promise.all([
        getCurrentUser(),
        fetchAuthSession(),
        fetchMFAPreference(),
      ])
      setUser(currentUser)
      setSession(authSession)
      setHasMFA(mfaPreference.preferred === 'TOTP')
      resetPersistedUserSpecificValues(currentUser.username)
    } catch (error) {
      // Only sign out for genuine auth errors (e.g. invalid/expired refresh token).
      // Transient network errors (e.g. screen sleep on Android) should not force logout.
      if (isNetworkError(error)) {
        console.warn(
          '[Auth] Network error during auth check, keeping current session',
          error,
        )
      } else {
        // signing out to make sure we have a clean state
        await signOut()
        setUser(null)
        setSession(null)
        setHasMFA(null)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'tokenRefresh':
          checkAuthState()
          break
        case 'tokenRefresh_failure':
          // Token refresh can fail due to transient network issues (e.g. screen sleep on Android).
          // Retry once before giving up — if the retry also fails, checkAuthState handles it gracefully.
          console.warn('[Auth] Token refresh failed, retrying auth check')
          checkAuthState()
          break
        case 'signedOut':
          setUser(null)
          setSession(null)
          break
      }
    })

    // We can only configure Amplify after we set up our auth listeners to avoid a race condition
    // where we miss the initial signIn events (https://github.com/aws-amplify/amplify-js/issues/13436)
    configureAmplify(userPoolClientId)

    checkAuthState()

    return unsubscribe
  }, [checkAuthState, userPoolClientId])

  // Proactively refresh auth state when the app regains visibility.
  // On Android tablets, the screen sleeping suspends network and can expire tokens.
  // This ensures a fresh session is obtained before any API calls are made.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAuthState()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [checkAuthState])

  const claims = useMemo(
    () =>
      token
        ? Claims.fromWireClaims(jwtDecode(token), {
            defaultBOUM: {},
            defaultCognito: {},
            defaultEmployee: {},
            defaultOpenID: {},
          })
        : null,
    [token],
  )

  return (
    <AuthContext.Provider
      value={{
        claims,
        token,
        isAuthenticated,
        hasMFA,
        loading,
        checkAuthState,
      }}
    >
      <RestrictedContext.Provider
        value={claims?.boum || FromBoumWireClaims({}, {})}
      >
        {children}
      </RestrictedContext.Provider>
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

const isNetworkError = (error: unknown): boolean => {
  if (error instanceof Error && error.name === 'NetworkError') {
    return true
  }
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return true
  }
  return false
}

const resetPersistedUserSpecificValues = (currentUsername: string) => {
  const lastUser = store.get(LAST_AUTHENTICATED_USER)
  store.set(LAST_AUTHENTICATED_USER, currentUsername)

  const currentUserIsDifferent = lastUser && currentUsername !== lastUser
  if (currentUserIsDifferent) {
    store.remove(SERVICE_AREA_MANAGEMENT_SERVICE_AREA_ID_KEY)
    store.remove(MAP_FILTERS_VISIBLE_KEY)
    store.remove(POOLING_MAP_TOGGLE_TRAFFIC_LAYER_KEY)
    store.remove(DARK_MODE_KEY)
    store.remove(NAV_OPEN_FLAG_KEY)
    store.remove(REACT_I18NEXT_LANGUAGE_KEY)
    store.remove(REGIONS_KEY)
  }
}
